import { DateTime } from "luxon";
import { z } from "zod";

import { GetEventFromSlug } from "../actions";
import { EventJourneyStatus } from "@prisma/client";

export const getDefaultFormValues = (
    event: GetEventFromSlug,
): EditFormSchema => {
    // Create DateTime objects in the event's timezone
    const startDateTime = DateTime.fromJSDate(event.startTime, {
        zone: "UTC",
    }).setZone("local", { keepLocalTime: true });
    const endDateTime = DateTime.fromJSDate(event.endTime ?? event.startTime, {
        zone: "UTC",
    }).setZone("local", { keepLocalTime: true });

    const isHidden = event.protectedFields?.find(
        (field) => field.name === "location",
    );

    const pseudoLocation = isHidden?.value;

    return {
        image: event.image,
        eventName: event.name,
        visibility: "Public",
        isTemplate: event.isTemplate,
        isMicroAutoApproved: event.isMicroAutoApproved,
        hostId: event.community
            ? { type: "community", communityId: event.community.id }
            : { type: "user", userId: event.user?.id ?? "" },
        startDate: {
            date: startDateTime.toJSDate(),
            time: startDateTime.toFormat("HH:mm"),
        },
        endDate: {
            date: endDateTime.toJSDate(),
            time: endDateTime.toFormat("HH:mm"),
        },
        location: event.location
            ? {
                  type: "physical",
                  address: event.location ?? "",
                  name: event.locationName ?? "",
                  googleMapsPlaceId: event.googleMapsPlaceId ?? "",
                  googleMapsPlaceName: event.googleMapsPlaceName ?? "",
                  googleMapsPlaceTypes: event.googleMapsPlaceTypes ?? [],
                  useGoogleMapsLocation: event.useGoogleMapsLocation ?? false,
                  city: event.city ?? "",
                  country: event.country ?? "",
                  longitude: event.longitude ?? 0,
                  latitude: event.latitude ?? 0,
              }
            : {
                  type: "virtual",
                  link: event.virtualEventUrl ?? "",
              },
        showNotes: event.showNotes ?? "",
        commManagerNote: event.commManagerNotes ?? "",
        description: event.description ?? "",
        tags: event.tags ?? [],
        resourceTags: event.resourceTags ?? [],
        timezone: event.timezone,
        isPreapproved: event.isPreapproved ?? false,
        capacity:
            event.capacity === null ? "unlimited" : event.capacity?.toString(),
        eventJourneyStatus: event.eventJourneyStatus,
        membershipTierId: event.membershipTierId,
        groupChatUrl: event.groupChatUrl ?? "",
        isLocationHidden: !!isHidden,
        pseudoLocation:
            typeof pseudoLocation === "string" ? pseudoLocation : "",
        timeout: event.protectedTiemout ?? 0,
        isTimeSet: event.isTimeSet ?? true,
    };
};

export const editFormSchema = z.object({
    image: z.string().nullable(),
    eventName: z.string().transform((val) => val.slice(0, 140).trim()),
    visibility: z.enum(["Public", "Private"]).optional(),
    hostId: z.discriminatedUnion("type", [
        z.object({ type: z.literal("user"), userId: z.string() }),
        z.object({ type: z.literal("community"), communityId: z.string() }),
    ]),
    startDate: z.object({
        date: z.date(),
        time: z.string(),
    }),
    endDate: z.object({
        date: z.date(),
        time: z.string(),
    }),
    location: z.discriminatedUnion("type", [
        z.object({
            type: z.literal("physical"),
            name: z.string().optional(),
            address: z.string().optional(),
            link: z.string().optional(),
            googleMapsPlaceId: z.string().optional(),
            googleMapsPlaceName: z.string().optional(),
            googleMapsPlaceTypes: z.array(z.string()).optional(),
            useGoogleMapsLocation: z.boolean().optional(),
            city: z.string().optional(),
            country: z.string().optional(),
            longitude: z.number().optional(),
            latitude: z.number().optional(),
            utcOffset: z.number().optional(), // we don't save offset in db, but we use it to get the timezone.
        }),
        z.object({
            type: z.literal("virtual"),
            link: z.string(),
        }),
    ]),
    isTemplate: z.boolean().nullish(),
    isMicroAutoApproved: z.boolean(),
    isPreapproved: z.boolean().optional(),
    showNotes: z.string().optional(),
    commManagerNote: z.string().optional(),
    description: z.string().nullable(),
    tags: z.array(z.string()),
    resourceTags: z.array(z.string()).optional(),
    timezone: z.string().optional(),
    capacity: z.string().optional().default("100"),
    eventJourneyStatus: z
        .enum(
            Object.values(EventJourneyStatus) as [
                keyof typeof EventJourneyStatus,
            ],
        )
        .optional(),
    membershipTierId: z.string().nullable(),
    groupChatUrl: z.string().optional(),
    isMatchMakerEvent: z.boolean().optional(),
    isLocationHidden: z.boolean().optional(),
    pseudoLocation: z.string().optional(),
    timeout: z.number().optional(),
    isTimeSet: z.boolean().optional(),
});

export type EditFormSchema = z.infer<typeof editFormSchema>;
